import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors & token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const res = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
          const { accessToken } = res.data.data;
          localStorage.setItem('accessToken', accessToken);
          processQueue(null, accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch {
          processQueue(error, null);
          localStorage.clear();
          window.location.href = '/login';
        } finally {
          isRefreshing = false;
        }
      } else {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API helpers
export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  verifyEmail: (token) => api.get(`/auth/verify/${token}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.put('/auth/me/password', data),
};

export const exams = {
  getAll: (params) => api.get('/exams', { params }),
  getAvailable: () => api.get('/exams/available'),
  getOne: (id) => api.get(`/exams/${id}`),
  create: (data) => api.post('/exams', data),
  update: (id, data) => api.put(`/exams/${id}`, data),
  delete: (id) => api.delete(`/exams/${id}`),
  publish: (id, status) => api.patch(`/exams/${id}/publish`, { status }),
  getMonitoring: (id) => api.get(`/exams/${id}/monitoring`),
  registerCandidates: (id, candidateIds) => api.post(`/exams/${id}/register`, { candidateIds }),
};

export const questions = {
  getByExam: (examId) => api.get(`/questions/exam/${examId}`),
  add: (examId, data) => api.post(`/questions/exam/${examId}`, data),
  update: (id, data) => api.put(`/questions/${id}`, data),
  delete: (id) => api.delete(`/questions/${id}`),
  importCSV: (examId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/questions/exam/${examId}/import-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  reorder: (examId, order) => api.put(`/questions/exam/${examId}/reorder`, { order }),
};

export const attempts = {
  start: (examId) => api.post(`/attempts/exam/${examId}/start`),
  saveAnswer: (attemptId, data) => api.put(`/attempts/${attemptId}/answer`, data),
  submit: (attemptId) => api.post(`/attempts/${attemptId}/submit`),
  reportViolation: (attemptId, data) => api.post(`/attempts/${attemptId}/violation`, data),
  getResult: (attemptId) => api.get(`/attempts/${attemptId}/result`),
};

export const results = {
  getAll: (params) => api.get('/results', { params }),
  getCandidate: (candidateId) => api.get(`/results/candidate/${candidateId || ''}`),
  getStats: () => api.get('/results/stats'),
  export: (params) => api.get('/results/export', { params, responseType: 'blob' }),
  shortlist: (data) => api.post('/results/shortlist', data),
  shortlistExcel: (data) => api.post('/results/shortlist', { ...data, format: 'excel' }, { responseType: 'blob' }),
};

export const admin = {
  getCandidates: (params) => api.get('/admin/candidates', { params }),
  toggleCandidate: (id) => api.patch(`/admin/candidates/${id}/toggle`),
  inviteCandidates: (examId, candidateIds) => api.post(`/admin/exams/${examId}/invite`, { candidateIds }),
};

export const superAdmin = {
  getStats: () => api.get('/super-admin/stats'),
  getAdmins: () => api.get('/super-admin/admins'),
  createAdmin: (data) => api.post('/super-admin/admins', data),
  toggleAdmin: (id) => api.patch(`/super-admin/admins/${id}/toggle`),
  getSettings: () => api.get('/super-admin/settings'),
  updateSettings: (settings) => api.put('/super-admin/settings', { settings }),
};

export const users = {
  uploadPhoto: (file) => {
    const fd = new FormData(); fd.append('photo', file);
    return api.post('/users/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadResume: (file) => {
    const fd = new FormData(); fd.append('resume', file);
    return api.post('/users/me/resume', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getAll: (params) => api.get('/users', { params }),
};

export default api;
